import React, {useLayoutEffect, useRef, useState} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ObjUtil, Util} from 'yougile-util'
import {
  DataObjectId,
  getDefaultProjectPermissions,
  HubData,
  PermissionCustomRole,
  PermissionObject,
  permissionObjectToChild
} from 'yougile-model'
import {PermissionsHelper} from '@client/permissions-helper'
import {Sync} from '@client/sync'
import {ButtonNew, LinkButtonNew} from '@components/common/base/buttons'
import {InputNew} from '@components/common/base/input-new'
import {Popup} from '@components/common/base/popup'
import {
  arePermissionsChangedForObject,
  getPermissionsFromRoleForObject,
  getPermissionsFromRoleForObjectFromParent
} from '@components/project-participants/helpers'
import {RoleObjectsTree} from '@components/project-participants/role-objects-tree'
import {useBoolean} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {PermissionsSettings} from '../permissions/permissions-settings/permissions-settings'

const TwError = tw.div`
  text-sm-semibold
  text-status-error
`

const TwBackCnt = tw.div`
  flex
  items-center
  gap-20
  py-8
  mt-8
  mb-20
`

const TwNameCnt = tw.div`
  flex
  gap-20
  mb-20
`

const TwTable = tw.div<{$changed?: boolean}>`
  flex-1
  flex
  border-solid
  border-1
  rounded-4
  overflow-hidden
  ${p => cn(p.$changed ? 'border-status-error' : 'border-line-base')}
`

const TwHeader = tw.div`
  h-52
  p-16
  text-16
  leading-5
  font-semibold
  text-primary
  bg-background-secondary
  select-none
`

const TwColumn = tw.div`
  min-w-0
  flex-1
  flex
  flex-col
`

const TwSettingsColumn = tw(TwColumn)`
  border-solid
  border-0
  border-l-1
  border-line-base
`

interface Props {
  projectId: DataObjectId
  isNewProject: boolean
  roles: HubData['roles']
  initialRole: PermissionCustomRole
  saveRole(role: PermissionCustomRole): void
  back(): void
  close(): void
}

// eslint-disable-next-line max-lines-per-function
export const RoleEdit = (props: Props) => {
  const {projectId, roles, initialRole, isNewProject} = props
  const {saveRole, close, back} = props

  const nameInputRef = useRef<HTMLInputElement>(null)

  const box = Sync.getCurrentSandbox()
  const [role, setRole] = React.useState(Util.clone(initialRole))
  const unsaved = useBoolean(false)
  const unsavedName = useBoolean(false)
  const errorName = useBoolean(false)
  const unsavedDescr = useBoolean(false)
  const unsavedPerm = useBoolean(false)
  const isNew = !(roles ?? {})[initialRole.key]
  const notChanged = !isNew && ObjUtil.deepEqual(role, initialRole, true)
  const save = () => {
    unsaved.turnOff()
    unsavedName.turnOff()
    unsavedDescr.turnOff()
    unsavedPerm.turnOff()

    if (!role.name.trim()) {
      errorName.turnOn()
      nameInputRef.current?.focus()
    } else {
      saveRole(role)
    }
  }

  let error: string | undefined
  if (errorName.value) {
    error = Lang.msg.proj.roleNameEmpty
  } else if (unsaved.value) {
    error = Lang.msg.errorNotSaveData
  }

  const footer = (
    <div className="flex justify-end items-center gap-12">
      {error && <TwError>{error}</TwError>}
      <ButtonNew
        color="secondary"
        title={Lang.msg.proj.cancel}
        onClick={back}
      />
      <ButtonNew
        disabled={notChanged && !unsaved.value}
        title={Lang.msg.proj.saveRole}
        onClick={save}
      />
    </div>
  )

  const [selected, setSelected] = React.useState<{
    id: DataObjectId
    type: PermissionObject['type']
  }>({id: projectId, type: 'project'})

  const isAvailable = (id: DataObjectId) =>
    isNewProject || PermissionsHelper.canEditPermissions(box, id)

  const changeObject = (obj: PermissionObject) => {
    const children = {...role.permissions.children}
    const newRole: PermissionCustomRole = {
      ...role,
      permissions: {...role.permissions, children}
    }

    if (obj.objectId === projectId && obj.type === 'project') {
      newRole.permissions = {...obj.permissions, children}
    } else if (obj.type !== 'project') {
      const permissions = permissionObjectToChild(obj)
      if (permissions) {
        const defaultPermissions = getPermissionsFromRoleForObjectFromParent(
          role,
          obj.objectId,
          obj.type
        )
        if (
          ObjUtil.deepEqual(
            permissions.permissions,
            defaultPermissions.permissions,
            true
          )
        ) {
          delete children[obj.objectId] // If object's permissions are default ones, remove it
        } else {
          children[obj.objectId] = permissions
        }
      }
    }

    // If a parent is changed, all children permissions may now equal to default
    // ones and should be removed
    for (const childId of Object.keys(children)) {
      const defaultPermissions = getPermissionsFromRoleForObjectFromParent(
        newRole,
        childId as DataObjectId,
        children[childId].type
      )
      if (
        ObjUtil.deepEqual(
          children[childId].permissions,
          defaultPermissions.permissions,
          true
        )
      ) {
        delete children[childId]
      }
    }

    setRole(newRole)
  }

  const changeName = (text: string) => {
    setRole({...role, name: text})
    errorName.turnOff()
  }
  const changeDescr = (text: string) => {
    setRole({...role, description: text})
  }

  const checkSaved = (): boolean => {
    if (ObjUtil.deepEqual(initialRole, role, true)) {
      return true
    }

    unsaved.turnOn()
    unsavedName.setValue(initialRole.name !== role.name)
    unsavedDescr.setValue(initialRole.description !== role.description)
    unsavedPerm.setValue(
      !ObjUtil.deepEqual(initialRole.permissions, role.permissions, true)
    )
    return false
  }

  const backWithWarn = () => {
    if (checkSaved()) {
      back()
    }
  }

  const closeWithWarn = () => {
    if (checkSaved()) {
      close()
    }
  }

  const revert = () => {
    if (selected.id === projectId) {
      setRole(role => {
        const children = {...role.permissions.children}
        const permissions = getDefaultProjectPermissions()
        return {...role, permissions: {...permissions, children}}
      })
    } else {
      setRole(role => {
        const children = {...role.permissions.children}
        if (children && selected.id in children) {
          delete children[selected.id]
        }
        return {...role, permissions: {...role.permissions, children}}
      })
    }
  }

  const object = getPermissionsFromRoleForObject(
    role,
    selected.id,
    selected.type
  )

  const [initialExpandedIds] = useState(() => {
    const expandedIds = new Set([projectId])
    const children = role.permissions.children ?? {}
    for (const id of Object.keys(children)) {
      const entity = box.get(id as DataObjectId)
      if (entity) {
        const ancestors = box.getAllParentHubs(entity)
        for (const ancestor of ancestors) {
          expandedIds.add(ancestor.id)
        }
      }
    }
    return expandedIds
  })

  const scrollBarRef = useRef<ScrollBar | null>(null)
  useLayoutEffect(() => {
    scrollBarRef.current?.updateScroll()
  }, [role])

  return (
    <Popup
      close={closeWithWarn}
      title={isNew ? Lang.msg.proj.newRole : Lang.msg.proj.editRole}
      width={920}
      autoHeight={false}
      footer={footer}
      scrollBarRef={scrollBarRef}
    >
      <TwBackCnt>
        <LinkButtonNew
          icon="IconArrowLeft"
          title={Lang.msg.back}
          onClick={backWithWarn}
          disabled={unsaved.value}
        />
      </TwBackCnt>
      <TwNameCnt>
        <InputNew
          ref={nameInputRef}
          label={Lang.msg.proj.roleName}
          placeholder={Lang.msg.proj.roleNamePl}
          value={role.name}
          onChange={changeName}
          error={errorName.value}
          autoFocus
          className="flex-1"
        />
        <InputNew
          label={Lang.msg.proj.roleDescr}
          placeholder={Lang.msg.proj.roleDescrPl}
          value={role.description}
          onChange={changeDescr}
          className="flex-1"
        />
      </TwNameCnt>
      <TwTable $changed={unsavedPerm.value}>
        <TwColumn>
          <TwHeader>{Lang.msg.permissionsObjectsHeader}</TwHeader>
          <RoleObjectsTree
            role={role}
            projectId={projectId}
            isNewProject={isNewProject}
            selectedId={selected.id}
            setSelected={(id, type) => setSelected({id, type})}
            initialExpandedIds={initialExpandedIds}
          />
        </TwColumn>
        <TwSettingsColumn>
          <TwHeader>{Lang.msg.permissionsObjectSettingsHeader}</TwHeader>
          {!!selected && (
            <PermissionsSettings
              isNew={isNew}
              isChanged={arePermissionsChangedForObject(
                role,
                selected.id,
                selected.type
              )}
              object={object}
              onChange={changeObject}
              isAvailable={isAvailable}
              revert={revert}
            />
          )}
        </TwSettingsColumn>
      </TwTable>
    </Popup>
  )
}
